import { BusinessObjectBase } from './../../bo';
import { ModelBase, PropertyType } from './../../model';

import { DeepReadonly } from '@skylab-shared/types';
import { RuleDefinition, RuleResult } from './rules-engine';

export abstract class AttachedRule<TRuleResult, TRuleArgs = undefined> {
    public constructor( protected readonly ruleDefinition: RuleDefinition<TRuleArgs>,
                        protected readonly bo: BusinessObjectBase<ModelBase>,
                        protected readonly source: ModelBase,
                        protected readonly target: ModelBase) { }

    public get ruleId(): string {
        return this.ruleDefinition.id;
    }

    public get args(): DeepReadonly<TRuleArgs> {
        return this.ruleDefinition.args;
    }

    public attachedToSource(source: ModelBase): boolean {
        return this.source === source;
    }

    public attachedToTarget(target: ModelBase): boolean {
        return this.target === target;
    }

    public attachedTo(source: ModelBase, target: ModelBase): boolean {
        return this.attachedToSource(source) && this.attachedToTarget(target);
    }

    public triggeredBy<TModel extends ModelBase>([type, propertyName]: PropertyType<TModel>): boolean {
        return this.ruleDefinition.triggeredBy.some(prop => (prop[0] === type || prop[0].isPrototypeOf(type)) && prop[1] === propertyName);
    }

    public effectOn<TModel extends ModelBase>([type, propertyName]: PropertyType<TModel>): boolean {
        return this.ruleDefinition.effectOn.some(prop => (prop[0] === type || prop[0].isPrototypeOf(type)) && prop[1] === propertyName);
    }

    public async invoke(): Promise<(TRuleResult & RuleResult)[]> {
        const violation = await this.validateRule();
        if (violation) {
            return this.ruleDefinition.effectOn.map(prop => Object.assign(violation, {
                source: this.target,
                propertyName: prop[1],
                id: this.ruleId
            }));
        }
        return [];
    }

    public invokeSync(): (TRuleResult & RuleResult)[] {
        const violation = this.validateRuleSync();
        if (violation) {
            return this.ruleDefinition.effectOn.map(prop => Object.assign(violation, {
                source: this.target,
                propertyName: prop[1],
                id: this.ruleId
            }));
        }
        return [];
    }

    public equals(attachedRule: AttachedRule<unknown, unknown>): boolean {
        return this.ruleId === attachedRule.ruleId
            && this.attachedTo(attachedRule.source, attachedRule.target);
    }

    protected abstract validateRule(): Promise<TRuleResult>;
    protected validateRuleSync(): TRuleResult {
        return null as any;
    };
}
